import { useState } from 'react';
import { getFocusedDifference } from 'store';
import { BrailleDetails, Difference, DifferenceTypeNames } from 'types';
import DecodedLayout from './DecodedLayout';
import { useSelector } from 'react-redux';
import { Box, Divider, Grid, Theme } from '@mui/material';
import { GVTypography } from 'components/lib';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import DifferenceGridElement from './DifferenceGridElement';
import { makeStyles } from 'tss-react/mui';
import { FailIcon } from './barcodeStatusUtils';
import { getCategoryColor } from 'pdftron/utils/barcodeGradeColors';
import { getStyleVariables } from 'styles/vars';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    metadataBox: {
      backgroundColor: styleVariables.colors.darkGreyBackground,
      borderRadius: '4px',
      padding: '8px',
      width: '100%',
    },
    metadataRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      padding: theme.spacing(1),
      borderRadius: '4px',
      transition: `background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms`,
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)',
      },
    },
    divider: {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    fontColorRed: {
      color: theme.palette.secondary.main,
    },
  };
});

interface BrailleLayoutProps {
  id?: string;
}

interface MetadataTableValue {
  key: string;
  value: number | string;
  isMarburgCompliant?: boolean;
}

const metadataLabels: { [key: string]: string } = {
  dots: 'Dots',
  characters: 'Characters',
  lines: 'Lines',
  dotHorizontalSpacing: 'Dot H Spacing',
  dotVerticalSpacing: 'Dot V Spacing',
  characterSpacing: 'Character Spacing',
  wordSpacing: 'Word Spacing',
  lineSpacing: 'Line Spacing',
};

const BrailleLayout = ({ id }: BrailleLayoutProps) => {
  const { classes } = useStyles();
  const currentDifference = useSelector(getFocusedDifference);
  const [decodedContainer, setDecodedContainer] = useState<HTMLDivElement | null>(null);

  const { brailleDetails } = currentDifference as Difference;
  if (brailleDetails === null) return null;

  const decodedWords = (
    <Box ref={setDecodedContainer}>
      <GVTypography emphasis="superhigh" component="div">
        {brailleDetails?.decodedTextLines.map((word, i) => (
          <Grid container key={i}>
            {word.split('').map((char, j) => (
              <Grid item key={j}>
                <DifferenceGridElement character={char} tooltipContainer={decodedContainer} />
              </Grid>
            ))}
          </Grid>
        ))}
      </GVTypography>
    </Box>
  );

  const createMetadataTableRows = (brailleDetails: BrailleDetails, tooltipContainer: HTMLDivElement | null) => {
    const metadataTableRows: MetadataTableValue[] = [
      {
        key: 'dots',
        value: brailleDetails.dots.detectedDots,
      },
      {
        key: 'characters',
        value: brailleDetails.characters,
      },
      {
        key: 'lines',
        value: brailleDetails.decodedTextLinesCount,
      },
    ];
    // Add the metadata values from the brailleMeasurements array
    metadataTableRows.push(...brailleDetails.brailleMeasurements);

    const rows = metadataTableRows.map((row, i) => {
      const addUnits = row.value !== 'N/A' && !['dots', 'characters', 'lines'].includes(row.key);
      const isFailure = row.value !== 'N/A' && row.isMarburgCompliant === false;
      const failColor = getCategoryColor('fail');

      return (
        <Box key={row.key}>
          <Box className={classes.metadataRow}>
            <GVTypography emphasis="medium" variant="subtitle1">
              {metadataLabels[row.key]}
            </GVTypography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isFailure && (
                <GVTooltip title="This measurement fails Marburg compliance criteria" container={tooltipContainer}>
                  <Box sx={{ display: 'flex' }}>
                    <FailIcon color={failColor} />
                  </Box>
                </GVTooltip>
              )}
              <GVTypography variant="subtitle1">{addUnits ? `${row.value} mm` : row.value}</GVTypography>
            </Box>
          </Box>
          {i !== metadataTableRows.length - 1 && <Divider className={classes.divider} />}
        </Box>
      );
    });

    return <Box className={classes.metadataBox}>{rows}</Box>;
  };

  return (
    <DecodedLayout
      id={id}
      type={DifferenceTypeNames.Braille}
      decodedValue={decodedWords}
      metaData={(container) => createMetadataTableRows(brailleDetails as BrailleDetails, container)}
    />
  );
};

export default BrailleLayout;
